import axios, { AxiosInstance, AxiosRequestHeaders } from 'axios';
import { API_VERSION } from '../utils/constants';

export const isNetworkError = (error: any) => {
    return error.code === 'ERR_NETWORK';
};

axios.interceptors.response.use();

export class ApiInstance {
    private static instance: AxiosInstance;
    private static token: string;

    private static refreshToken: string;
    private static isRefreshing = false;
    private static refreshSubscribers: ((token: string) => void)[] = [];

    private static async refreshAuthToken() {
        try {
            const userInfoTemp = localStorage.getItem('userInfo');
            const userInfoParse = userInfoTemp ? JSON.parse(userInfoTemp) : {};
            this.refreshToken = userInfoParse.refreshToken ?? this.refreshToken;
            const header = {
                Authorization: `Bearer ${this.refreshToken}`,
            };
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/${API_VERSION}/api/auth/refresh`,
                {},
                {
                    headers: header,
                }
            );
            const { accessToken, refreshToken } = response.data;
            this.setTokens(accessToken, refreshToken);
            return accessToken;
        } catch (error) {
            localStorage.removeItem('userInfo');
            window.location.href = '/login';
            return null;
        }
    }

    private static subscribeTokenRefresh(cb: (token: string) => void) {
        this.refreshSubscribers.push(cb);
    }

    private static onTokenRefreshed(token: string) {
        this.refreshSubscribers.forEach(cb => cb(token));
        this.refreshSubscribers = [];
    }

    public static getInstance(): AxiosInstance {
        if (!this.instance) {
            const token = localStorage.getItem('token') ?? this.token;
            const headers: AxiosRequestHeaders = {
                'Content-Type': 'application/json',
            } as AxiosRequestHeaders;
            if (token !== undefined) {
                headers.Authorization = `Bearer ${token}`;
            }

            this.instance = axios.create({
                baseURL: `${process.env.REACT_APP_API_BASE_URL}/${API_VERSION}`,
                headers,
                withCredentials: false,
                timeout: 30000,
            });

            // Add a request interceptor
            this.instance.interceptors.request.use(
                config => {
                    if (config.headers) {
                        const token =
                            localStorage.getItem('token') ?? this.token;
                        if (token !== undefined) {
                            config.headers['Authorization'] = `Bearer ${token}`;
                        }
                    }
                    return config;
                },
                error => {
                    return Promise.reject(error);
                }
            );

            // Response interceptor
            this.instance.interceptors.response.use(
                response => response,
                async error => {
                    const originalRequest = error.config;

                    if (
                        error.response?.status === 401 &&
                        !originalRequest._retry
                    ) {
                        if (this.isRefreshing) {
                            // Wait for token refresh
                            return new Promise(resolve => {
                                this.subscribeTokenRefresh((token: string) => {
                                    originalRequest.headers.Authorization = `Bearer ${token}`;
                                    resolve(axios(originalRequest));
                                });
                            });
                        }

                        originalRequest._retry = true;
                        this.isRefreshing = true;

                        const newToken = await this.refreshAuthToken();
                        this.isRefreshing = false;

                        if (newToken) {
                            this.onTokenRefreshed(newToken);
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                            return axios(originalRequest);
                        }
                    }
                    return Promise.reject(error);
                }
            );
        }
        return this.instance;
    }

    public static setTokens(accessToken: string, refreshToken: string) {
        this.token = accessToken;
        this.refreshToken = refreshToken;
        if (this.instance) {
            this.instance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        }
        localStorage.setItem(
            'userInfo',
            JSON.stringify({
                accessToken,
                refreshToken,
            })
        );
    }

    public static resetTokens() {
        this.token = '';
        this.refreshToken = '';
        this.instance.defaults.headers.common['Authorization'] = '';
        localStorage.removeItem('userInfo');
    }
}
